import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_QUANTITY_SETTLE_MS = 180;

/**
 * DAYMERIT cart transaction queue.
 * All custom cart writes share this queue so Shopify line keys and returned section HTML
 * are consumed in the same order as the mutations that produced them.
 * @param {() => Promise<any>} task
 * @returns {Promise<any>}
 */
function enqueueCartMutation(task) {
  const cartWindow = /** @type {any} */ (window);
  const previous = cartWindow.__daymeritCartMutationQueue || Promise.resolve();
  const current = previous.catch(() => undefined).then(task);
  cartWindow.__daymeritCartMutationQueue = current.catch(() => undefined);
  return current;
}

/** @returns {Map<string, number>} */
function getLineVersions() {
  const cartWindow = /** @type {any} */ (window);
  if (!(cartWindow.__daymeritCartLineVersions instanceof Map)) {
    cartWindow.__daymeritCartLineVersions = new Map();
  }
  return cartWindow.__daymeritCartLineVersions;
}

/** @param {string} key */
function bumpLineVersion(key) {
  const versions = getLineVersions();
  const next = (versions.get(key) || 0) + 1;
  versions.set(key, next);
  return next;
}

/** @param {string} key @param {number} version */
function isLatestLineVersion(key, version) {
  return getLineVersions().get(key) === version;
}

function supersededError() {
  return new DOMException('Superseded by a newer cart quantity', 'AbortError');
}

/**
 * A custom element that displays and updates cart items.
 *
 * DAYMERIT mobile stability rules:
 * - The input changes immediately on every tap, but network writes are coalesced for 180ms.
 * - A burst of 5 taps therefore produces one final cart/change request instead of 5 requests.
 * - A new tap immediately invalidates an older in-flight response, so that response cannot
 *   repaint an older quantity over the user's latest value.
 * - Removes are flushed immediately and cancel a pending +/- burst for that line.
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * Pending per-line network writes. The quantity selector itself remains fully interactive.
   * @type {Map<string, {line:number, quantity:number, action:string, lineId:string, versionKey:string, version:number, timer:number|null}>}
   */
  #pendingQuantityUpdates = new Map();

  /** @param {Event} event */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(async ({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];

        if (sectionsHtml) {
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Discount update rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];

        if (sectionsHtml) {
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Note update rejected:', error);
      });
  };

  /** @param {Event} event */
  #handleQuantitySelectorUpdate = (event) => {
    if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    for (const pending of this.#pendingQuantityUpdates.values()) {
      if (pending.timer != null) window.clearTimeout(pending.timer);
    }
    this.#pendingQuantityUpdates.clear();
  }

  /** @param {QuantitySelectorUpdateEvent} event */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    this.#scheduleQuantityUpdate({
      line,
      quantity,
      action: quantity === 0 ? 'clear' : 'change',
      immediate: quantity === 0,
    });

    const lineItemRow = this.refs.cartItemRows?.[line - 1];
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Schedule a final server write for a burst of taps. The version is bumped NOW, not after
   * the delay, so an older in-flight response is instantly made stale and cannot morph the DOM.
   * @param {{line:number, quantity:number, action:string, immediate?:boolean}} config
   */
  #scheduleQuantityUpdate(config) {
    const lineId = this.refs.cartItemRows?.[config.line - 1]?.dataset.key ?? '';
    const versionKey = lineId || `line:${config.line}`;
    const version = bumpLineVersion(versionKey);

    const previous = this.#pendingQuantityUpdates.get(versionKey);
    if (previous?.timer != null) window.clearTimeout(previous.timer);

    const pending = {
      line: config.line,
      quantity: config.quantity,
      action: config.action,
      lineId,
      versionKey,
      version,
      timer: /** @type {number|null} */ (null),
    };

    this.#pendingQuantityUpdates.set(versionKey, pending);

    const flush = () => {
      if (this.#pendingQuantityUpdates.get(versionKey) !== pending) return;
      this.#pendingQuantityUpdates.delete(versionKey);
      this.updateQuantity(pending);
    };

    if (config.immediate) {
      flush();
    } else {
      pending.timer = window.setTimeout(flush, CART_QUANTITY_SETTLE_MS);
    }
  }

  /**
   * Deletion is server-confirmed and flushes immediately. There is intentionally no optimistic
   * empty-cart DOM swap.
   * @param {number} line
   */
  onLineItemRemove(line) {
    this.#scheduleQuantityUpdate({ line, quantity: 0, action: 'clear', immediate: true });
  }

  /**
   * @param {{line:number, quantity:number, action:string, lineId:string, versionKey:string, version:number}} config
   */
  updateQuantity(config) {
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { line, quantity, lineId, versionKey, version } = config;

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    enqueueCartMutation(async () => {
      try {
        if (!isLatestLineVersion(versionKey, version)) throw supersededError();

        const requestBody = {
          quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
          ...(lineId ? { id: lineId } : { line }),
        };

        const response = await fetch(
          `${Theme.routes.cart_change_url}`,
          fetchConfig('json', { body: JSON.stringify(requestBody) })
        );

        const responseText = await response.text();
        const parsedResponse = JSON.parse(responseText);

        resetShimmer(this);

        if (!isLatestLineVersion(versionKey, version)) throw supersededError();

        if (!response.ok || parsedResponse.errors) {
          const message =
            typeof parsedResponse.errors === 'string'
              ? parsedResponse.errors
              : parsedResponse.description || parsedResponse.message || 'Unable to update cart';

          this.#handleCartError(line, { errors: message });
          throw new Error(message);
        }

        this.#updateQuantitySelectors(parsedResponse);

        const sectionHtml = parsedResponse.sections?.[this.sectionId];
        let newCartItemCount = Number(parsedResponse.item_count ?? 0);

        if (sectionHtml) {
          const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
          const hiddenCount = parsedSection.querySelector('[ref="cartItemCount"]')?.textContent;
          if (hiddenCount != null && hiddenCount.trim() !== '') {
            const parsedCount = Number(hiddenCount);
            if (Number.isFinite(parsedCount)) newCartItemCount = parsedCount;
          }

          await morphSection(this.sectionId, sectionHtml, { mode: 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: 'full' });
        }

        this.#updateCartQuantitySelectorButtonStates();

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponse),
          detail: {
            sections: parsedResponse.sections,
            items: parsedResponse.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
            uiSynced: true,
          },
        });
      } catch (error) {
        deferredUpdatePromise.reject(error);

        if (error?.name !== 'AbortError') {
          this.dispatchEvent(
            new CartErrorEvent({
              error: error?.message || 'Failed to update cart',
              code: 'SERVICE_UNAVAILABLE',
            })
          );
        }
      } finally {
        cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
      }
    });
  }

  /**
   * @param {number} line
   * @param {{errors: string}} parsedResponse
   */
  #handleCartError = (line, parsedResponse) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) cartItemError.textContent = parsedResponse.errors;
    if (cartItemErrorContainer instanceof HTMLElement) cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponse.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /** @param {CartLinesUpdateEvent} event */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError) return;
        if (detail?.items) this.#updateQuantitySelectors({ items: detail.items });

        if (detail?.uiSynced) {
          this.#updateCartQuantitySelectorButtonStates();
          return;
        }

        const sectionHtml = detail?.sections?.[this.sectionId];
        if (sectionHtml) {
          await morphSection(this.sectionId, sectionHtml, { mode: 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: 'full' });
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Cart update rejected:', error);
      });
  };

  /** @param {{items?: Array<{variant_id: number, quantity: number}>}} updatedCart */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      return response.json();
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
